import { useQuery } from '@apollo/client/react'
import { VStack, Text, Heading, Stack, Icon, SimpleGrid } from '@chakra-ui/react'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { gql } from '@src/lib/gql'
import { useHover } from '@uidotdev/usehooks'
import type { ElementType } from 'react'
import { CgMediaPodcast } from 'react-icons/cg'

import { RiAppleFill, RiSpotifyFill, RiYoutubeFill } from 'react-icons/ri'

type PodcastSubscribeProps = {
  title?: string
  number?: number
  iTunesLink?: string
  tuneInLink?: string
  spotifyLink?: string
  stitcherLink?: string
}

type PodcastLinkProps = {
  link: string
  icon: ElementType
  label: string
  bgColor?: string
  hoverColor?: string
}

const PodcastLink = ({ link, icon, label, bgColor, hoverColor }: PodcastLinkProps) => {
  const [ref, hovering] = useHover()
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <NextLink href={link} prefetch={false} target={'_blank'}>
      <Stack
        ref={ref}
        bgColor={'colorPalette.container'}
        direction={getBreakpointValue(['column', 'column', 'column', 'row', 'row'])}
        height={'full'}
        overflow={'hidden'}
        padding={10}
        position={'relative'}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: `radial-gradient(${bgColor}, transparent)`,
          aspectRatio: 1,
          opacity: 0,
          transition: 'opacity 0.2s ease-in',
        }}
        _hover={{
          _before: {
            opacity: 1,
          },
        }}
      >
        <Stack
          align={'center'}
          direction={getBreakpointValue(['column', 'column', 'column', 'row', 'row'])}
          filter={hovering ? 'drop-shadow(0 0 0.5rem rgba(0, 0, 0, 0.5))' : null}
          height={'full'}
          justify={'center'}
          transform={hovering ? 'scale(1.1)' : 'scale(1)'}
          transition={'all 0.2s ease-in-out'}
          width={'full'}
        >
          <Icon as={icon} boxSize={10} color={hovering ? hoverColor : 'actionPrimary'} />
          <Text
            hidden={getBreakpointValue([true, false])}
            textAlign={getBreakpointValue(['center', 'center', 'center', 'left', 'left'])}
            textStyle={getBreakpointValue(['labelMd', 'labelMd', 'labelMd', 'labelMd', 'labelLg'])}
          >
            {label}
          </Text>
        </Stack>
      </Stack>
    </NextLink>
  )
}

export const PodcastSubscribe = (props: PodcastSubscribeProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const podcastQuery = useQuery(
    gql`
      query {
        podcastEpisodes(first: 1, orderBy: releaseDate_DESC) {
          id
          title
          description
          slug
          releaseDate
          number
          posterImage {
            id
            url
            altText
            height
            width
          }
          megaphoneLink
          transistorLink
          iTunesLink
          tuneInLink
          spotifyLink
          stitcherLink
        }
      }
    `,
    { variables: {} }
  )

  const episode = podcastQuery.data?.podcastEpisodes[0]

  const podcastLinks = [
    {
      link: props.iTunesLink || episode?.iTunesLink || '#',
      icon: RiAppleFill,
      label: 'Podcasts',
      bgColor: '#872ec4',
    },
    {
      link: props.spotifyLink || episode?.spotifyLink || '#',
      icon: RiSpotifyFill,
      label: 'Spotify',
      bgColor: '#1DB954',
    },
    {
      link: props.tuneInLink || episode?.tuneInLink || '#',
      icon: CgMediaPodcast,
      label: 'TuneIn',
      bgColor: '#14D8CC',
    },
    {
      link: 'https://www.youtube.com/@StartupTherapy',
      icon: RiYoutubeFill,
      label: 'YouTube',
      bgColor: 'red',
    },
  ]

  return (
    <VStack align={'flex-start'} gap={8} width={'full'}>
      {props.title && (
        <Heading size={'2xl'} textAlign={'center'} width={'full'}>
          {props.title}
        </Heading>
      )}
      <SimpleGrid
        borderRadius={'lg'}
        columns={getBreakpointValue([2, 2, 2, 4, 4])}
        gap={1}
        overflow={'hidden'}
        width={'full'}
      >
        {podcastLinks.map((podcastLink, index) => (
          <PodcastLink
            key={index}
            bgColor={podcastLink.bgColor}
            icon={podcastLink.icon}
            label={podcastLink.label}
            link={podcastLink.link}
          />
        ))}
      </SimpleGrid>
    </VStack>
  )
}
